import * as Sentry from '@sentry/react'

import { redact } from '$extensionSrc/utils/pii'

import { FACEBOOK_AUTOMATION_STAGE_STATUSES } from './facebookAutomationStageStatuses'
import {
  forcedAccountSwitchUrl,
  getAccountsCenterUrl,
  getAccountsCenterEditUsernameUrl,
  homeUrl,
  accountsCenterUrl,
  profileUrl,
} from './fbUrls'
import { updatePlatformUserAccountData } from '../../data/platform/platformUserAccountData'
import { PLATFORM, StageType, TriggeringEventType } from '../../utils/enums'
import { StageDef } from '../automationMachineInterfaces'
import { getUrlChangePromise } from '../interruptiblePromises'
import { evaluateXpath } from '../interruptibleXpath'
import { NeedsUserInputError } from '../needsInputError'
import { getElement, getOptionalElement } from '../utils'

enum StageKey {
  GATHER_FACEBOOK_ACCOUNT_TRY_ACCOUNT_MENU = 'GATHER_FACEBOOK_ACCOUNT_TRY_ACCOUNT_MENU',
  GATHER_FACEBOOK_ACCOUNT_GO_TO_ACCOUNT_CENTER = 'GATHER_FACEBOOK_USERNAME_GO_TO_ACCOUNT_CENTER',
  GATHER_FACEBOOK_ACCOUNT_NOT_PAGE_CHECK = 'GATHER_FACEBOOK_PROFILE_NOT_PAGE',
  GATHER_FACEBOOK_NAME_GO_TO_PROFILES = 'GATHER_FACEBOOK_NAME_GO_TO_PROFILES',
  GATHER_FACEBOOK_NAME = 'GATHER_FACEBOOK_NAME',
  GATHER_FACEBOOK_USERNAME_GO_TO_EDIT_USERNAME = 'GATHER_ACCOUNT_USERNAME_GO_TO_EDIT_USERNAME',
  GATHER_FACEBOOK_USERNAME = 'GATHER_FACEBOOK_USERNAME',
  GATHER_FACEBOOK_ACCOUNT_SAVE_USERNAME = 'GATHER_FACEBOOK_ACCOUNT_SAVE_USERNAME',
}

// Look for menu in upper right hand corner

function getLinksUnderProfileMenu(): string[] {
  const elements = evaluateXpath<HTMLAnchorElement>(
    document,
    '//div[@aria-label="Your profile"]/descendant::a',
  )

  return elements.map((element) => redact(element.href))
}

/**
 * Opens profile menu in upper right hand corner.
 *
 * There should be a link to /me/ in that menu, whose label is the name.
 *
 * By "name" we mean display name like "Blobby McParty".
 */
export async function getNameAccountMenuMethod(): Promise<string> {
  const openAccountMenuButton = await getElement<HTMLDivElement>(
    document,
    '//div[@role = "button" and @aria-label = "Your profile"]',
  )

  openAccountMenuButton.click()

  let profileLinkLabel
  try {
    profileLinkLabel = await getElement<HTMLElement>(
      document,
      '//*[@href="/me/"]/descendant::*[text()]',
    )
  } catch (error) {
    let links: string[] = []
    let foundProfileMenu = false
    try {
      foundProfileMenu =
        evaluateXpath(
          document,
          '//div[@role = "dialog" and @aria-label = "Your profile"]',
        ).length > 0
      links = getLinksUnderProfileMenu()
    } catch {
      // pass
    }

    Sentry.setContext('Automation - Profile Menu Details', {
      profileMenuLinks: links,
      foundProfileMenu,
    })

    throw error
  }

  return profileLinkLabel.textContent
}

// Account center utils

async function getAccountCenterModalNameLink(
  rootNode: Node,
): Promise<HTMLElement | null> {
  return getOptionalElement<HTMLElement>(
    rootNode,
    'descendant::*[contains(@aria-label, "Name")]',
  )
}

function getValueFromInputGivenLabel(
  documentObject: Document,
  labelElement: HTMLLabelElement,
): string | null {
  let inputElement

  inputElement = documentObject.getElementById(
    labelElement.htmlFor,
  ) as HTMLInputElement
  if (!inputElement) {
    inputElement = evaluateXpath<HTMLInputElement>(
      labelElement,
      `ancestor::*/descendant::input[@name = "${labelElement.htmlFor}"]`,
    )[0]
  }

  if (!inputElement) {
    return null
  }

  return inputElement.value || ''
}

async function waitForLabelWithText(
  rootNode: Node,
  textVariations: string[],
): Promise<HTMLLabelElement | null> {
  const textMatchingClauses = textVariations
    .map((text) => `starts-with(text(), "${text}")`)
    .join(' or ')
  return getElement<HTMLLabelElement>(
    rootNode,
    `descendant::label[${textMatchingClauses}]`,
  )
}

async function getUsernameFromAccountCenterModal(
  rootNode: Node,
  documentObject: Document,
): Promise<string> {
  const labelElement = await waitForLabelWithText(rootNode, [
    'Username',
    'username',
    'User name',
    'User Name',
  ])

  if (!labelElement) {
    throw new Error('Could not find label element for username')
  }

  const value = getValueFromInputGivenLabel(documentObject, labelElement)

  return value
}

async function getNameFromAccountCenterModal(
  rootNode: Node,
  documentObject: Document,
): Promise<string> {
  const nameSegmentMap = {
    first: ['First name', 'first name', 'First Name', 'FIRST NAME'],
    middle: ['Middle name', 'middle name', 'Middle Name', 'MIDDLE NAME'],
    last: ['Last name', 'last name', 'Last Name', 'LAST NAME', 'Surname'],
  }

  const nameSegmentValuesPromises = Object.entries(nameSegmentMap).map(
    async ([key, labelTexts]) => {
      const labelElement = await waitForLabelWithText(rootNode, labelTexts)

      if (!labelElement) {
        throw new Error(`Could not find label element for name segment ${key}`)
      }

      const value = getValueFromInputGivenLabel(documentObject, labelElement)
      if (value === null) {
        throw new Error(`Could not find value for name segment ${key}`)
      }

      return [key, value.trim()]
    },
  )

  const nameSegmentValuesMap = Object.fromEntries(
    await Promise.all(nameSegmentValuesPromises),
  )

  // Facebook requires first and last, but not middle
  if (nameSegmentValuesMap.middle) {
    return (
      `${nameSegmentValuesMap.first} ` +
      `${nameSegmentValuesMap.middle} ` +
      `${nameSegmentValuesMap.last}`
    )
  }
  return `${nameSegmentValuesMap.first} ${nameSegmentValuesMap.last}`
}

export const gatherAccountUsernameStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.GATHER_FACEBOOK_ACCOUNT_TRY_ACCOUNT_MENU,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.INITIALIZING_PLAYBOOK,
    transitionNextStageMap: {
      [TriggeringEventType.ON_ERROR]:
        StageKey.GATHER_FACEBOOK_ACCOUNT_GO_TO_ACCOUNT_CENTER,
      [TriggeringEventType.ON_EVENT_ACTION_COMPLETED]:
        StageKey.GATHER_FACEBOOK_ACCOUNT_SAVE_USERNAME,
    },
    url: profileUrl,
    urlPattern: /.+\.facebook\.com\/.+/,
    action: async (machineContext, userContext, additionalMachineData) => {
      const { persistentData } = additionalMachineData

      const name = await getNameAccountMenuMethod()

      if (!name) {
        throw new Error(
          'facebookGatherAccountUsername: No name found, although method succeeded',
        )
      }

      await persistentData.set({
        ...persistentData.data,
        username: name,
      })
    },
  },
  {
    type: StageType.ACTION,
    key: StageKey.GATHER_FACEBOOK_ACCOUNT_GO_TO_ACCOUNT_CENTER,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.INITIALIZING_PLAYBOOK,
    action: async (machineContext, userContext, additionalMachineData) => {
      /**
       * This stage is a pivot point; it navigates to the account center URLs
       * and waits for the next page to load
       *
       * It uses `persistentData` to track what actions it has taken (in
       * particular, to prevent it from trying to continually reload the
       * account center pages).
       */
      const { persistentData } = additionalMachineData

      if (!persistentData.data.hasNavigatedToAccountsCenter) {
        await persistentData.set({
          ...persistentData.data,
          hasNavigatedToAccountsCenter: true,
        })

        const waitForUrlChange = getUrlChangePromise()
        window.location.assign(accountsCenterUrl)
        await waitForUrlChange
      }
    },
  },
  {
    type: StageType.ACTION,
    key: StageKey.GATHER_FACEBOOK_ACCOUNT_NOT_PAGE_CHECK,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.INITIALIZING_PLAYBOOK,
    action: async (machineContext, userContext, additionalMachineData) => {
      const { persistentData } = additionalMachineData

      const currentUrl = window.location.href

      // If not at the account center, short circuit
      if (!currentUrl.startsWith(accountsCenterUrl)) {
        if (currentUrl.startsWith(forcedAccountSwitchUrl)) {
          throw new NeedsUserInputError('Switch account page detected')
        } else {
          throw new Error(
            `Did not navigate to ${accountsCenterUrl}. At ${currentUrl} instead`,
          )
        }
      }

      persistentData.clear()
    },
  },
  {
    type: StageType.ACTION,
    key: StageKey.GATHER_FACEBOOK_NAME_GO_TO_PROFILES,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.INITIALIZING_PLAYBOOK,
    transitionNextStageMap: {
      [TriggeringEventType.ON_ERROR]:
        StageKey.GATHER_FACEBOOK_USERNAME_GO_TO_EDIT_USERNAME,
    },
    action: async (machineContext, userContext, additionalMachineData) => {
      /**
       * This stage is a pivot point; it navigates to the account center URLs
       * and waits for the next page to load
       *
       * It uses `persistentData` to track what actions it has taken (in
       * particular, to prevent it from trying to continually reload the
       * account center pages).
       */
      const { userId } = userContext
      const { persistentData } = additionalMachineData

      if (!persistentData.data.hasNavigatedToAccountsCenterProfiles) {
        await persistentData.set({
          ...persistentData.data,
          hasNavigatedToAccountsCenterProfiles: true,
        })
        const accountsCenterUserUrl = getAccountsCenterUrl(userId)

        const waitForUrlChange = getUrlChangePromise()
        window.location.assign(accountsCenterUserUrl)
        await waitForUrlChange
      }
    },
  },
  {
    type: StageType.ACTION,
    key: StageKey.GATHER_FACEBOOK_NAME,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.INITIALIZING_PLAYBOOK,
    transitionNextStageMap: {
      [TriggeringEventType.ON_ERROR]:
        StageKey.GATHER_FACEBOOK_USERNAME_GO_TO_EDIT_USERNAME,
      [TriggeringEventType.ON_EVENT_ACTION_COMPLETED]:
        StageKey.GATHER_FACEBOOK_ACCOUNT_SAVE_USERNAME,
    },
    action: async (machineContext, userContext, additionalMachineData) => {
      const { userId } = userContext
      const { persistentData } = additionalMachineData

      const currentUrl = window.location.href
      const expectedUrlBase = getAccountsCenterUrl(userId)

      // If not at the account center, short circuit
      if (!currentUrl.startsWith(expectedUrlBase)) {
        throw new Error(
          `Did not navigate to ${expectedUrlBase}. At ${currentUrl} instead.`,
        )
      }

      const nameLink = await getAccountCenterModalNameLink(document)
      if (!nameLink) {
        // Fetch all aria labels for debugging purposes
        const ariaLabels = evaluateXpath<HTMLElement>(
          document,
          '//*[@role = "dialog"]//*[@aria-label]',
        ).map((x) => x?.ariaLabel)

        Sentry.setContext('Automation - Account Center Modal', {
          foundAriaLabels: ariaLabels,
        })

        throw new Error(
          'Could not find nameLink in Facebook account center modal',
        )
      }

      const waitForUrlChangeName = getUrlChangePromise()

      nameLink.click()
      await waitForUrlChangeName

      const name = await getNameFromAccountCenterModal(document, document)
      if (name) {
        await persistentData.set({
          ...persistentData.data,
          username: name,
        })
      }
    },
  },
  {
    type: StageType.ACTION,
    key: StageKey.GATHER_FACEBOOK_USERNAME_GO_TO_EDIT_USERNAME,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.INITIALIZING_PLAYBOOK,
    action: async (machineContext, userContext, additionalMachineData) => {
      /**
       * This stage is a pivot point; it navigates to the account center URLs
       * and waits for the next page to load
       *
       * It uses `persistentData` to track what actions it has taken (in
       * particular, to prevent it from trying to continually reload the
       * account center pages).
       */
      const { userId } = userContext
      const { persistentData } = additionalMachineData

      if (!persistentData.data.hasNavigatedToAccountsCenterEditUsername) {
        await persistentData.set({
          ...persistentData.data,
          hasNavigatedToAccountsCenterEditUsername: true,
        })
        const accountsCenterUserUrl = getAccountsCenterEditUsernameUrl(userId)

        const waitForUrlChange = getUrlChangePromise()
        window.location.assign(accountsCenterUserUrl)
        await waitForUrlChange
      }
    },
  },
  {
    type: StageType.ACTION,
    key: StageKey.GATHER_FACEBOOK_USERNAME,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.INITIALIZING_PLAYBOOK,
    action: async (machineContext, userContext, additionalMachineData) => {
      /**
       * If on the account center, collect the data
       */
      const { userId } = userContext
      const { persistentData } = additionalMachineData

      const currentUrl = window.location.href
      const expectedUrlBase = getAccountsCenterEditUsernameUrl(userId)

      // If not at the account center, short circuit
      if (!currentUrl.startsWith(expectedUrlBase)) {
        throw new Error(
          `Did not navigate to ${expectedUrlBase}. At ${currentUrl} instead.`,
        )
      }

      // Get username
      const username = await getUsernameFromAccountCenterModal(
        document,
        document,
      )

      if (username) {
        await persistentData.set({
          ...persistentData.data,
          username,
        })
      } else {
        throw new Error('Could not gather username in account center modal')
      }
    },
  },
  {
    type: StageType.ACTION,
    key: StageKey.GATHER_FACEBOOK_ACCOUNT_SAVE_USERNAME,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.INITIALIZING_PLAYBOOK,
    url: homeUrl,
    action: async (machineContext, userContext, additionalMachineData) => {
      const { platform, userId } = userContext
      const { persistentData } = additionalMachineData

      const username = persistentData?.data?.username

      if (username) {
        await updatePlatformUserAccountData(platform as PLATFORM, userId, {
          username: persistentData.data.username,
        })
      }

      persistentData.clear()
    },
  },
]
